import { useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import type { TokenDef } from '../services/interviewLinksApi';
import { LinkTemplateEditor } from './LinkTemplateEditor';
import { hasLinkTo, linkWordsInText } from './linkEditorDom';

type Lang = 'de' | 'en';
const LABEL: Record<Lang, string> = { de: 'German', en: 'English' };

/**
 * The German and English bodies of one candidate-email template, side by side and aware of each
 * other's links (Sofia, Oct 8 2026).
 *
 * "Apply this for the English version" on a German link (and the reverse) first looks for the
 * same words in the other text — a URL label is often identical in both, e.g. a product name — and
 * links them directly. When they are not there, the other editor opens its URL row with the
 * address filled in and asks the author to pick the words in that language. Leaving the box
 * unticked is allowed; the editor then warns that the other version has no such link.
 */
export function TemplateBodyEditors({
  tokens,
  rows,
  de,
  en,
  setDe,
  setEn,
}: {
  tokens: TokenDef[];
  rows: number;
  de: string;
  en: string;
  setDe: (v: string) => void;
  setEn: (v: string) => void;
}) {
  const [request, setRequest] = useState<{ lang: Lang; href: string; nonce: number } | null>(null);
  const value: Record<Lang, string> = { de, en };
  const set: Record<Lang, (v: string) => void> = { de: setDe, en: setEn };

  /** A link was applied in `from`; carry it over to the other language. */
  const carryOver = (from: Lang) => (href: string, words: string) => {
    const to: Lang = from === 'de' ? 'en' : 'de';
    if (hasLinkTo(value[to], href)) {
      showToast({ status: 'success', description: `The ${LABEL[to]} version already links to this address.` });
      return;
    }
    const linked = linkWordsInText(value[to], words, href);
    if (linked !== null) {
      set[to](linked);
      showToast({ status: 'success', description: `Also linked “${words}” in the ${LABEL[to]} version.` });
      return;
    }
    setRequest({ lang: to, href, nonce: Date.now() });
    showToast({
      status: 'info',
      description: `“${words}” is not in the ${LABEL[to]} version — select the words there and press Apply.`,
    });
  };

  return (
    <>
      {(['de', 'en'] as const).map((lang) => {
        const other: Lang = lang === 'de' ? 'en' : 'de';
        return (
          <LinkTemplateEditor
            key={lang}
            label={LABEL[lang]}
            tokens={tokens}
            rows={rows}
            value={value[lang]}
            onChange={set[lang]}
            pair={{ label: LABEL[other], value: value[other], onApply: carryOver(lang) }}
            linkRequest={request?.lang === lang ? { href: request.href, nonce: request.nonce } : null}
          />
        );
      })}
    </>
  );
}
